/**
 * `growth/config` — PURE resolution of the ads configuration.
 *
 * Same three-layer pattern as the monetization kit
 * (`monetization/config.ts`): **code defaults → env → admin UI overrides**.
 * Env and UI share one source: env supplies the default and each override
 * overrides ONE key live; deleting the override returns control to env.
 *
 * **OFF by default is sacred**: without `ADS_ENABLED`/`VITE_ADS_ENABLED` the
 * entire layer is inert, `network` is `none`, and no SDK is loaded.
 */

import { adTierFloor, hardenAdRegime, isAdTier } from "./regime";
import type { AdContentRating, AdInitOptions, AdsConfig, AdTier } from "./types";
import { AD_CONTENT_RATINGS } from "./types";

/** `none` backend: no-op. It is the DEFAULT and guarantees "no ads, no SDK". */
export const AD_NETWORK_NONE = "none";

/**
 * Backends known to the kit. `none` first on purpose.
 * `fake` is the deterministic provider for e2e suites (no network or DOM): it is
 * NEVER configured in a real environment.
 */
export const AD_NETWORKS = [AD_NETWORK_NONE, "adsense", "fake"] as const;

/** Admin UI override keys (same source as env). */
export const AD_OVERRIDE_KEYS = [
  "ads.enabled",
  "ads.network",
  "ads.tier",
  "ads.child_directed",
  "ads.max_content_rating",
  "ads.client_id",
] as const;

export type AdOverrideKey = (typeof AD_OVERRIDE_KEYS)[number];

export function isValidAdOverrideKey(key: string): key is AdOverrideKey {
  return (AD_OVERRIDE_KEYS as readonly string[]).includes(key);
}

/** Code defaults: everything off. Enabling ads is an explicit decision. */
export function builtinAdsDefaults(): AdsConfig {
  return {
    enabled: false,
    network: AD_NETWORK_NONE,
    tier: "A",
    childDirected: false,
    maxAdContentRating: "G",
    clientId: "",
  };
}

function parseBool(v: string | undefined): boolean | undefined {
  if (v === undefined) {
    return undefined;
  }
  const s = v.trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(s)) {
    return true;
  }
  if (["0", "false", "no", "off"].includes(s)) {
    return false;
  }
  return undefined;
}

function parseTier(v: string | undefined): AdTier | undefined {
  const s = v?.trim().toUpperCase();
  return s && isAdTier(s) ? s : undefined;
}

function isAdContentRating(v: string): v is AdContentRating {
  return (AD_CONTENT_RATINGS as readonly string[]).includes(v);
}

function parseRating(v: string | undefined): AdContentRating | undefined {
  const s = v?.trim().toUpperCase();
  return s && isAdContentRating(s) ? s : undefined;
}

/**
 * Layer 2: env. Both monorepo forms are accepted — `ADS_*` (server and e2e
 * suites) and `VITE_ADS_*` (web bundle) — with Vite winning on the
 * client, where the build is authoritative.
 */
export function applyAdsEnv(config: AdsConfig, env: Record<string, string | undefined>): void {
  const enabled = parseBool(env.VITE_ADS_ENABLED) ?? parseBool(env.ADS_ENABLED);
  if (enabled !== undefined) {
    config.enabled = enabled;
  }
  const network = (env.VITE_ADS_NETWORK ?? env.ADS_NETWORK)?.trim().toLowerCase();
  if (network) {
    config.network = network;
  }
  const tier = parseTier(env.VITE_ADS_TIER ?? env.ADS_TIER);
  if (tier) {
    config.tier = tier;
  }
  const child = parseBool(env.VITE_ADS_CHILD_DIRECTED) ?? parseBool(env.ADS_CHILD_DIRECTED);
  if (child !== undefined) {
    config.childDirected = child;
  }
  const rating = parseRating(env.VITE_ADS_MAX_CONTENT_RATING ?? env.ADS_MAX_CONTENT_RATING);
  if (rating) {
    config.maxAdContentRating = rating;
  }
  const clientId = (env.VITE_ADS_CLIENT_ID ?? env.ADS_CLIENT_ID)?.trim();
  if (clientId) {
    config.clientId = clientId;
  }
}

/** Layer 3: a back-office override (same semantics as monetization). */
export function applyAdsOverride(config: AdsConfig, key: string, value: unknown): void {
  switch (key) {
    case "ads.enabled": {
      if (typeof value === "boolean") {
        config.enabled = value;
      }
      return;
    }
    case "ads.network": {
      if (typeof value === "string" && value.trim()) {
        config.network = value.trim().toLowerCase();
      }
      return;
    }
    case "ads.tier": {
      const tier = parseTier(typeof value === "string" ? value : undefined);
      if (tier) {
        config.tier = tier;
      }
      return;
    }
    case "ads.child_directed": {
      if (typeof value === "boolean") {
        config.childDirected = value;
      }
      return;
    }
    case "ads.max_content_rating": {
      const rating = parseRating(typeof value === "string" ? value : undefined);
      if (rating) {
        config.maxAdContentRating = rating;
      }
      return;
    }
    case "ads.client_id": {
      if (typeof value === "string") {
        config.clientId = value.trim();
      }
      return;
    }
    default:
  }
}

/**
 * **Layer 4 — the regime clamp. Classification wins over the flag.**
 *
 * Adapter for the *env* and *admin override* paths: translates the resolved
 * `AdsConfig` into a regime request and hardens it against its tier floor with
 * [`hardenAdRegime`], the only place in the kit where policy is composed.
 *
 * What it guarantees:
 *   - tiers without ads (`C`, `D`): `enabled=false` and `network=none` ⇒ zero SDK;
 *   - child tiers (`K`): `childDirected=true` ALWAYS;
 *   - a rating ceiling per tier, and **`G` whenever there is a child regime**,
 *     regardless of where it comes from (the tier or an env that HARDENS a tier `A`).
 *
 * Idempotent and monotonic: applying it twice is the same, and it never increases.
 *
 * History: the C/D clamp existed from day one, but the child-tier clamp did not —
 * `VITE_ADS_CHILD_DIRECTED=false` was enough to serve behavioral ads to a minor.
 * `kid-games-app` discovered this (the only tier-K app), and it was generalized
 * here in the canon so no clone depends on remembering it.
 */
export function clampAdsConfigToTier(config: AdsConfig): void {
  const floor = adTierFloor(config.tier);
  const regime = hardenAdRegime(floor, {
    adsAllowed: config.enabled,
    childDirected: config.childDirected,
    maxAdContentRating: config.maxAdContentRating,
  });
  config.enabled = regime.adsAllowed;
  if (!floor.adsAllowed) {
    // It is the TIER that prohibits ads ⇒ the network is not shipped either. An
    // env `enabled=false`, however, leaves the network configured: turning ads
    // off must not erase which network you would return to when turning them on.
    config.network = AD_NETWORK_NONE;
  }
  config.childDirected = regime.childDirected;
  config.maxAdContentRating = regime.maxAdContentRating;
}

/** Complete resolution: defaults → env → overrides → regime clamp. */
export function resolveAdsConfig(
  env: Record<string, string | undefined>,
  overrides: readonly { key: string; value: unknown }[] = [],
): AdsConfig {
  const config = builtinAdsDefaults();
  applyAdsEnv(config, env);
  for (const o of overrides) {
    applyAdsOverride(config, o.key, o.value);
  }
  clampAdsConfigToTier(config);
  return config;
}

/**
 * Init options HARDENED against the resolved config.
 *
 * The second hole in the same family: `WebAdProvider.init(opts)` took
 * `opts.childDirected` as-is, so a caller could undo the regime the clamp had
 * just guaranteed. Here the caller can only **harden** (request child-directed,
 * or a lower rating); relaxing it is impossible.
 *
 * Adapter for the *call options* path: the floor is the **already clamped**
 * config (which in turn is never more lenient than its tier), and composition
 * is done by [`hardenAdRegime`] as everywhere else.
 */
export function hardenAdInitOptions(config: AdsConfig, opts: AdInitOptions = {}): AdInitOptions {
  const regime = hardenAdRegime(adsConfigAsRegimeFloor(config), {
    childDirected: opts.childDirected,
    maxAdContentRating: opts.maxAdContentRating,
  });
  return {
    childDirected: regime.childDirected,
    maxAdContentRating: regime.maxAdContentRating,
    // Does not affect policy: passed through unchanged.
    ...(opts.testDeviceIds ? { testDeviceIds: opts.testDeviceIds } : {}),
  };
}

/**
 * The resolved config viewed as the regime floor. Numeric limits do not travel
 * in `AdsConfig` (they are operation, not commercial configuration: `adcaps.*`),
 * so on this path the capping floor is neutral and provided by the gate.
 */
function adsConfigAsRegimeFloor(config: AdsConfig) {
  return {
    adsAllowed: config.enabled,
    childDirected: config.childDirected,
    maxAdContentRating: config.maxAdContentRating,
    caps: adTierFloor(config.tier).caps,
  };
}

/**
 * Should this app load the ads kit? Combines the ads config with the verdict
 * from the monetization kit (`shouldShowAds(config, tier)` wins: premium ⇒ 0 ads
 * immediately). Without `enabled` or with network `none`, **zero SDK**.
 */
export function adsAreLive(config: AdsConfig, monetizationSaysAds: boolean): boolean {
  return config.enabled && config.network !== AD_NETWORK_NONE && monetizationSaysAds;
}
